import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  CheckCircle2,
  RefreshCw,
  LogOut,
  Users,
  Home,
  Info,
  MoreVertical,
  Crown,
  UserMinus,
  AlertTriangle,
  Bell,
  BellOff,
  BellRing,
  HelpCircle,
  Share2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import { useToast } from '../../contexts/ToastContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Avatar from '../../components/ui/Avatar';
import { ROUTES } from '../../config/constants';

/**
 * Configuración del hogar y la cuenta.
 *
 * Conecta la lógica que vivía huérfana en FamilyContext:
 *  - inviteCode visible y copiable.
 *  - isOwner para mostrar acción "Generar código nuevo" solo al owner.
 *  - rotateInviteCode (solo owner).
 *  - leaveFamily con confirmación.
 *
 * Modelo de identidad familiar al 2026-05-17: un usuario pertenece a UN solo
 * hogar (1:N). Multi-familia evaluada y postpuesta — ver ARCHITECTURE.md
 * §7 ADR-001. El footer discovery anticipa la feature sin code de respaldo.
 */
const SettingsPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const {
    family,
    members,
    inviteCode,
    isOwner,
    isLoading,
    leaveFamily,
    rotateInviteCode,
    kickMember,
    transferOwnership,
  } = useFamily();
  const toast = useToast();
  const {
    isSupported: notifSupported,
    permission: notifPermission,
    currentToken: notifCurrentToken,
    activating: notifActivating,
    activate: activateNotifications,
    deactivate: deactivateNotifications,
  } = useNotifications();

  const [copied, setCopied] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [notifDeactivating, setNotifDeactivating] = useState(false);
  const [showNotifHelp, setShowNotifHelp] = useState(false);

  // Estado del menú contextual de un miembro (3 puntitos).
  // openMenuFor: uid del miembro con menú abierto, o null.
  const [openMenuFor, setOpenMenuFor] = useState(null);

  // Estado de las confirmaciones de acciones owner-only.
  // Cada uno guarda el miembro target o null.
  const [confirmKick, setConfirmKick] = useState(null);
  const [confirmTransfer, setConfirmTransfer] = useState(null);
  const [kicking, setKicking] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // Cierre del menú al click fuera. Patrón estándar para popovers.
  const menuRef = useRef(null);
  useEffect(() => {
    if (!openMenuFor) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuFor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuFor]);

  const handleCopy = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      toast.success('Código copiado.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar. Probá manualmente.');
    }
  };

  const handleRotate = async () => {
    try {
      setRotating(true);
      await rotateInviteCode();
      toast.success('Código actualizado. El anterior ya no funciona.');
      setConfirmRotate(false);
    } catch (err) {
      toast.error(err.message || 'No se pudo generar un código nuevo.');
    } finally {
      setRotating(false);
    }
  };

  const handleLeave = async () => {
    try {
      setLeaving(true);
      await leaveFamily();
      toast.success('Abandonaste el hogar.');
      // refreshProfile dentro de leaveFamily ya limpia el familyId. El
      // ProtectedRoute con requireFamily redirige a /onboarding solo.
      navigate(ROUTES.ONBOARDING, { replace: true });
    } catch (err) {
      // Casos esperados: si el caller es owner con miembros activos, el
      // service rechaza con OWNER_HAS_MEMBERS. Mostramos el mensaje literal.
      toast.error(err.message || 'No se pudo abandonar el hogar.');
      setLeaving(false);
    }
  };

  const handleKick = async () => {
    if (!confirmKick) return;
    try {
      setKicking(true);
      await kickMember(confirmKick.id);
      toast.success(`${confirmKick.displayName || 'El miembro'} fue expulsado.`);
      setConfirmKick(null);
    } catch (err) {
      toast.error(err.message || 'No se pudo expulsar al miembro.');
    } finally {
      setKicking(false);
    }
  };

  const handleTransfer = async () => {
    if (!confirmTransfer) return;
    try {
      setTransferring(true);
      await transferOwnership(confirmTransfer.id);
      toast.success(
        `${confirmTransfer.displayName || 'El miembro'} ahora es owner.`
      );
      setConfirmTransfer(null);
    } catch (err) {
      toast.error(err.message || 'No se pudo transferir el hogar.');
    } finally {
      setTransferring(false);
    }
  };

  const handleActivateNotifications = async () => {
    const result = await activateNotifications();
    if (result.ok) {
      toast.success('Notificaciones activadas en este dispositivo.');
      return;
    }
    // PERMISSION_DEFAULT (cerró el diálogo) es esperable y silencioso.
    if (result.error?.code === 'PERMISSION_DEFAULT') return;

    // IOS_REQUIRES_PWA: el modal de ayuda con instrucciones es más útil que
    // un toast efímero. El usuario necesita pasos visuales para "Agregar a
    // pantalla de inicio".
    if (result.error?.code === 'IOS_REQUIRES_PWA') {
      toast.info(result.error.message, { duration: 8000 });
      return;
    }

    // PERMISSION_BLOCKED: ya hay un modal de ayuda dedicado en la UI cuando
    // el state es 'denied'. Pero este código viene cuando getToken detecta
    // que el permiso se revocó EN EL MEDIO del flow — mostramos el modal.
    if (result.error?.code === 'PERMISSION_BLOCKED' || result.error?.code === 'PERMISSION_DENIED') {
      setShowNotifHelp(true);
      return;
    }

    // El resto de códigos ya tienen un message user-friendly específico en
    // el service. Lo mostramos directo con duración un poco más larga para
    // que el usuario pueda leer la instrucción accionable.
    toast.error(
      result.error?.message || 'No se pudo activar las notificaciones.',
      { duration: 6000 }
    );
  };

  const handleDeactivateNotifications = async () => {
    try {
      setNotifDeactivating(true);
      await deactivateNotifications();
      toast.success('Notificaciones desactivadas en este dispositivo.');
    } catch (err) {
      toast.error(err.message || 'No se pudo desactivar las notificaciones.');
    } finally {
      setNotifDeactivating(false);
    }
  };

  if (isLoading || !family) {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={ArrowLeft}
          onClick={() => navigate(-1)}
          className="self-start"
        >
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  const ownerOnlyAndHasMembers = isOwner && members.length > 1;

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="self-start"
      >
        Volver
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
        <p className="text-sm text-slate-500">
          Hogar, miembros y código de invitación.
        </p>
      </div>

      {/* ===== Tu hogar ===== */}
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-lg bg-brand-100 p-2.5">
            <Home className="h-5 w-5 text-brand-700" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-slate-900 truncate">{family.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {members.length === 1
                ? '1 miembro'
                : `${members.length} miembros`}
              {isOwner && ' · sos owner'}
            </p>
          </div>
        </div>

        {/* Código de invitación */}
        {inviteCode ? (
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-700 mb-2">
              Código de invitación
            </p>
            <div className="rounded-xl border-2 border-dashed border-brand-300 bg-brand-50 px-4 py-3 flex items-center justify-between gap-2">
              <p className="font-mono text-xl font-bold tracking-[0.25em] text-brand-800 select-all">
                {inviteCode}
              </p>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={copied ? CheckCircle2 : Copy}
                onClick={handleCopy}
                aria-label="Copiar código"
              >
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Compartí este código con quien quieras invitar. Lo van a usar al
              ingresar a la app por primera vez.
            </p>
            {isOwner && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={RefreshCw}
                onClick={() => setConfirmRotate(true)}
                className="mt-3"
              >
                Generar código nuevo
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500 mb-4">
            No hay código de invitación disponible.
          </p>
        )}

        {/* Lista de miembros */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <p className="text-xs font-medium text-slate-700">
              Miembros del hogar
            </p>
          </div>
          <ul className="flex flex-col gap-2">
            {members.map((m) => {
              const isMe = m.id === profile?.uid;
              const isThisOwner = m.id === family.ownerId;
              // El menú de 3 puntitos aparece solo si: soy owner Y la fila
              // NO es la mía. El owner puede actuar sobre los demás miembros
              // pero no sobre sí mismo (para irse usa "Abandonar este hogar"
              // tras transferir ownership).
              const showActionsMenu = isOwner && !isMe;
              const isMenuOpen = openMenuFor === m.id;

              return (
                <li
                  key={m.id}
                  className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 relative"
                >
                  <Avatar src={m.photoURL} name={m.displayName} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {m.displayName || 'Sin nombre'}
                      {isMe && (
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          (vos)
                        </span>
                      )}
                    </p>
                    {m.email && (
                      <p className="text-xs text-slate-500 truncate">{m.email}</p>
                    )}
                  </div>
                  {isThisOwner && (
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-700 bg-brand-50 ring-1 ring-brand-100 rounded-full px-2 py-0.5">
                      Owner
                    </span>
                  )}
                  {showActionsMenu && (
                    <div className="relative" ref={isMenuOpen ? menuRef : null}>
                      <button
                        type="button"
                        onClick={() => setOpenMenuFor(isMenuOpen ? null : m.id)}
                        aria-label={`Acciones para ${m.displayName || 'miembro'}`}
                        aria-expanded={isMenuOpen}
                        aria-haspopup="menu"
                        className="rounded-full p-1.5 text-slate-500 hover:bg-slate-200 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4" aria-hidden="true" />
                      </button>
                      {isMenuOpen && (
                        <div
                          role="menu"
                          className="absolute right-0 top-full mt-1 z-10 w-52 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden"
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setOpenMenuFor(null);
                              setConfirmTransfer(m);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Crown className="h-4 w-4 text-amber-600" aria-hidden="true" />
                            Hacer owner
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setOpenMenuFor(null);
                              setConfirmKick(m);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-status-red hover:bg-red-50 transition-colors border-t border-slate-100"
                          >
                            <UserMinus className="h-4 w-4" aria-hidden="true" />
                            Expulsar del hogar
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </Card>

      {/* ===== Notificaciones ===== */}
      <NotificationsCard
        isSupported={notifSupported}
        permission={notifPermission}
        currentToken={notifCurrentToken}
        activating={notifActivating}
        deactivating={notifDeactivating}
        onActivate={handleActivateNotifications}
        onDeactivate={handleDeactivateNotifications}
        onShowHelp={() => setShowNotifHelp(true)}
      />

      {/* ===== Tu cuenta ===== */}
      <Card>
        <h2 className="font-semibold text-slate-900 mb-3">Tu cuenta</h2>
        <div className="flex items-center gap-3 mb-4">
          <Avatar src={profile?.photoURL} name={profile?.displayName} size="md" />
          <div className="min-w-0">
            <p className="font-medium text-slate-900 truncate">
              {profile?.displayName || 'Usuario'}
            </p>
            <p className="text-xs text-slate-500 truncate">{profile?.email}</p>
          </div>
        </div>

        <Button
          variant="danger"
          fullWidth
          leftIcon={LogOut}
          onClick={() => setConfirmLeave(true)}
        >
          Abandonar este hogar
        </Button>
        {ownerOnlyAndHasMembers && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 ring-1 ring-amber-100">
            Sos owner del hogar y todavía hay otros miembros. Para irte, primero
            transferí ownership o esperá a que el resto se vaya.
          </p>
        )}
      </Card>

      {/* ===== Footer discovery: multi-familia ===== */}
      <div className="flex items-start gap-3 rounded-xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3">
        <Info className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" aria-hidden="true" />
        <p className="text-xs text-slate-500">
          <strong className="text-slate-700">Próximamente:</strong> pertenecer a
          más de un hogar simultáneamente (ej. tu casa + la de tu pareja). Por
          ahora cada cuenta vive en un hogar a la vez.
        </p>
      </div>

      {/* ===== Modal: Confirmar rotación de código ===== */}
      <Modal
        isOpen={confirmRotate}
        onClose={() => !rotating && setConfirmRotate(false)}
        title="¿Generar un código nuevo?"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            Al generar uno nuevo, el código actual{' '}
            <strong className="font-mono">{inviteCode}</strong> deja de funcionar.
            Cualquiera que tenga el código viejo no va a poder unirse.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setConfirmRotate(false)}
              disabled={rotating}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              leftIcon={RefreshCw}
              isLoading={rotating}
              onClick={handleRotate}
            >
              Generar nuevo
            </Button>
          </div>
        </div>
      </Modal>

      {/* ===== Modal: Confirmar abandono ===== */}
      <Modal
        isOpen={confirmLeave}
        onClose={() => !leaving && setConfirmLeave(false)}
        title={`¿Abandonar ${family.name}?`}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            Vas a dejar de ver las mascotas y la bitácora de este hogar. Si
            querés volver, alguien del hogar va a tener que darte un código de
            invitación nuevo.
          </p>
          {ownerOnlyAndHasMembers && (
            <div className="rounded-lg bg-amber-50 px-3 py-2.5 text-xs text-amber-900 ring-1 ring-amber-100">
              Sos owner. El servicio va a rechazar la operación porque todavía
              hay otros miembros.
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setConfirmLeave(false)}
              disabled={leaving}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              fullWidth
              leftIcon={LogOut}
              isLoading={leaving}
              onClick={handleLeave}
            >
              Abandonar
            </Button>
          </div>
        </div>
      </Modal>

      {/* ===== Modal: Transferir ownership ===== */}
      <Modal
        isOpen={Boolean(confirmTransfer)}
        onClose={() => !transferring && setConfirmTransfer(null)}
        title={`¿Hacer owner a ${confirmTransfer?.displayName || 'este miembro'}?`}
      >
        <div className="flex flex-col gap-4">
          {confirmTransfer && (
            <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
              <Avatar
                src={confirmTransfer.photoURL}
                name={confirmTransfer.displayName}
                size="sm"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {confirmTransfer.displayName || 'Sin nombre'}
                </p>
                {confirmTransfer.email && (
                  <p className="text-xs text-slate-500 truncate">
                    {confirmTransfer.email}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
            <AlertTriangle
              className="h-5 w-5 shrink-0 text-amber-600 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-amber-900">
              Vas a ceder el control del hogar. Después de transferir no vas a
              poder rotar el código de invitación ni gestionar miembros (a menos
              que el nuevo owner te devuelva la propiedad).
            </p>
          </div>
          <p className="text-xs text-slate-500">
            Seguís siendo miembro y podés ver/usar todas las mascotas y
            registros como antes.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => setConfirmTransfer(null)}
              disabled={transferring}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              fullWidth
              leftIcon={Crown}
              isLoading={transferring}
              onClick={handleTransfer}
            >
              Transferir
            </Button>
          </div>
        </div>
      </Modal>

      {/* ===== Modal: Cómo activar notificaciones (browser denied) ===== */}
      <NotificationsHelpModal
        isOpen={showNotifHelp}
        onClose={() => setShowNotifHelp(false)}
      />

      {/* ===== Modal: Expulsar miembro ===== */}
      <Modal
        isOpen={Boolean(confirmKick)}
        onClose={() => !kicking && setConfirmKick(null)}
        title={`¿Expulsar a ${confirmKick?.displayName || 'este miembro'}?`}
      >
        <div className="flex flex-col gap-4">
          {confirmKick && (
            <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
              <Avatar
                src={confirmKick.photoURL}
                name={confirmKick.displayName}
                size="sm"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {confirmKick.displayName || 'Sin nombre'}
                </p>
                {confirmKick.email && (
                  <p className="text-xs text-slate-500 truncate">
                    {confirmKick.email}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="flex items-start gap-3 rounded-lg bg-red-50 px-3 py-2.5 ring-1 ring-red-100">
            <AlertTriangle
              className="h-5 w-5 shrink-0 text-status-red mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-red-900">
              Va a perder acceso inmediato al hogar y a todas las mascotas.
              Si querés que vuelva, vas a tener que invitarlo de nuevo con el
              código de invitación.
            </p>
          </div>
          <p className="text-xs text-slate-500">
            Su historial en la bitácora se preserva como registro inmutable,
            pero su nombre puede aparecer como "Alguien" en eventos antiguos.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => setConfirmKick(null)}
              disabled={kicking}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              fullWidth
              leftIcon={UserMinus}
              isLoading={kicking}
              onClick={handleKick}
            >
              Expulsar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SettingsPage;

/**
 * Card de notificaciones. Renderiza 5 estados:
 *  - isSupported === null: cargando (chequeando soporte).
 *  - permission === 'unsupported': browser no soporta FCM. Si es iOS no
 *    standalone, sugiere instalar como PWA.
 *  - permission === 'default': nunca se decidió. Botón para activar.
 *  - permission === 'granted': activadas. Botón para desactivar.
 *  - permission === 'denied': bloqueadas a nivel browser. Link al modal de ayuda.
 */
const NotificationsCard = ({
  isSupported,
  permission,
  currentToken,
  activating,
  deactivating,
  onActivate,
  onDeactivate,
  onShowHelp,
}) => {
  // Mientras chequea soporte, no mostrar nada para evitar parpadeo (es muy
  // rápido — un await de isMessagingSupported).
  if (isSupported === null) {
    return (
      <Card>
        <div className="flex items-center gap-3 mb-1">
          <div className="rounded-lg bg-slate-100 p-2.5">
            <Bell className="h-5 w-5 text-slate-500" aria-hidden="true" />
          </div>
          <h2 className="font-semibold text-slate-900">Notificaciones</h2>
        </div>
        <p className="text-sm text-slate-500 mt-2">Verificando soporte...</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start gap-3 mb-4">
        <div className="rounded-lg bg-brand-100 p-2.5">
          {permission === 'granted' ? (
            <BellRing className="h-5 w-5 text-brand-700" aria-hidden="true" />
          ) : (
            <Bell className="h-5 w-5 text-brand-700" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-slate-900">Notificaciones</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Avisos cuando le toca comer a tus mascotas.
          </p>
        </div>
      </div>

      {permission === 'unsupported' && <UnsupportedHint />}

      {permission === 'default' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-slate-600">
            Activá las notificaciones para que la app te avise cuando una
            mascota está por entrar en rojo o el stock se acerca al límite.
          </p>
          <Button
            variant="primary"
            leftIcon={Bell}
            isLoading={activating}
            onClick={onActivate}
          >
            Activar notificaciones
          </Button>
        </div>
      )}

      {permission === 'granted' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 ring-1 ring-emerald-100">
            <CheckCircle2
              className="h-4 w-4 shrink-0 text-emerald-600"
              aria-hidden="true"
            />
            <p className="text-sm text-emerald-900">
              {currentToken
                ? 'Activadas en este dispositivo.'
                : 'Tenés permiso, pero este dispositivo no está registrado todavía.'}
            </p>
          </div>
          {currentToken ? (
            <Button
              variant="secondary"
              leftIcon={BellOff}
              isLoading={deactivating}
              onClick={onDeactivate}
            >
              Desactivar en este dispositivo
            </Button>
          ) : (
            <Button
              variant="primary"
              leftIcon={Bell}
              isLoading={activating}
              onClick={onActivate}
            >
              Registrar este dispositivo
            </Button>
          )}
        </div>
      )}

      {permission === 'denied' && (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
            <AlertTriangle
              className="h-5 w-5 shrink-0 text-amber-600 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-amber-900">
              Las notificaciones están bloqueadas desde la configuración de tu
              navegador. No las podemos volver a pedir desde acá.
            </p>
          </div>
          <button
            type="button"
            onClick={onShowHelp}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 self-start"
          >
            <HelpCircle className="h-4 w-4" aria-hidden="true" />
            ¿Cómo activarlas?
          </button>
        </div>
      )}
    </Card>
  );
};

/**
 * Hint específico para el caso "no hay soporte". El subcaso más útil es iOS
 * Safari fuera de PWA — el sistema soporta Web Push desde iOS 16.4, pero solo
 * cuando la app está instalada como Home Screen app.
 */
const UnsupportedHint = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator?.standalone === true);

  if (isIOS && !isStandalone) {
    return (
      <div className="flex items-start gap-3 rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-slate-100">
        <Share2
          className="h-5 w-5 shrink-0 text-slate-500 mt-0.5"
          aria-hidden="true"
        />
        <div className="text-sm text-slate-700">
          <p className="font-medium">Agregá PetCare a tu pantalla de inicio</p>
          <p className="text-xs text-slate-500 mt-1">
            En iOS, las notificaciones solo funcionan cuando la app está
            instalada. Tocá el botón de compartir del navegador y elegí{' '}
            <strong>Agregar a inicio</strong>. Después volvé acá para activarlas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <p className="text-sm text-slate-500">
      Tu navegador no soporta notificaciones push. Probá con Chrome, Firefox o
      con un Safari actualizado.
    </p>
  );
};

/**
 * Modal con instrucciones para reactivar las notificaciones cuando el browser
 * las bloqueó. Detecta browser y muestra el flow más relevante arriba, pero
 * lista los tres principales para cubrir falsos negativos del detector.
 */
const NotificationsHelpModal = ({ isOpen, onClose }) => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isFirefox = /Firefox/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  const isChromium = /Chrome|Chromium|Edg/.test(ua);

  const sections = [
    {
      key: 'chrome',
      label: 'Chrome / Edge / Brave',
      active: isChromium,
      steps: [
        'En la barra de direcciones, tocá el ícono de candado (o de ajustes) a la izquierda de la URL.',
        'Buscá "Notificaciones" y cambialo a "Permitir".',
        'Volvé a esta página y tocá "Activar notificaciones".',
      ],
    },
    {
      key: 'firefox',
      label: 'Firefox',
      active: isFirefox,
      steps: [
        'Tocá el ícono de candado a la izquierda de la URL.',
        'Hacé clic en la flecha junto a "Permisos" y luego en "Notificaciones".',
        'Sacá el bloqueo y recargá la página.',
      ],
    },
    {
      key: 'safari',
      label: 'Safari (Mac / iOS PWA)',
      active: isSafari,
      steps: [
        'En Mac: Safari → Configuración → Sitios web → Notificaciones. Buscá esta página y poné "Permitir".',
        'En iOS: la app tiene que estar instalada desde el botón compartir → Agregar a inicio. Después: Ajustes → Notificaciones → PetCare.',
      ],
    },
  ];

  // El detectado arriba, los otros debajo. Si no detectamos ninguno, queda el
  // orden default.
  const ordered = [...sections].sort((a, b) =>
    a.active === b.active ? 0 : a.active ? -1 : 1
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="¿Cómo activar las notificaciones?">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          El navegador bloquea el permiso una vez que lo negaste. Hay que
          desbloquearlo manualmente desde la configuración del sitio.
        </p>
        {ordered.map((s) => (
          <div
            key={s.key}
            className={[
              'rounded-lg px-3 py-3 ring-1',
              s.active
                ? 'bg-brand-50 ring-brand-100'
                : 'bg-slate-50 ring-slate-100',
            ].join(' ')}
          >
            <p className="text-sm font-semibold text-slate-900 mb-2">
              {s.label}
              {s.active && (
                <span className="ml-2 text-xs font-normal text-brand-700">
                  (detectado)
                </span>
              )}
            </p>
            <ol className="flex flex-col gap-1.5 text-sm text-slate-700 list-decimal list-inside">
              {s.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        ))}
        <Button variant="secondary" fullWidth onClick={onClose}>
          Cerrar
        </Button>
      </div>
    </Modal>
  );
};
